// src/components/admin/Sales.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from "@mui/material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

/* =========================
 * 型
 * ========================= */
type RawOrder =
  | {
      id?: string | number;
      type?: string; // "charge" | "plan" など
      planId?: number;
      planName?: string;
      buyer?: string;
      email?: string;
      amount?: number; // 円 or pt
      pointSpent?: number;
      date?: string; // ISO
      purchasedAt?: string; // ISO
      createdAt?: string; // ISO
      userId?: number;
      affiliateId?: string | number | null;
      affiliateName?: string | null;
      priceJPY?: number;
    }
  | Record<string, any>;

type ChargeRow = {
  id: string;
  userId?: number;
  buyer: string;
  email?: string;
  amountJPY: number;
  chargedAt: string; // ISO
  affiliateId?: string | number | null;
  affiliateName?: string | null;
};

type PlanRow = {
  id: string;
  userId?: number;
  planId?: number;
  planName: string;
  amountPt: number; // プラン消費pt
  purchasedAt: string; // ISO
  affiliateId?: string | number | null;
  affiliateName?: string | null;
  buyer?: string;
};

/* =========================
 * ユーティリティ
 * ========================= */

// 日付フォーマット
const fmtYm = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const fmtYmd = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// date(yyyy-mm-dd) -> ISO範囲
const startOf = (d: string) => new Date(`${d}T00:00:00.000Z`).toISOString();
const endOf = (d: string) => {
  const dt = new Date(`${d}T00:00:00.000Z`);
  dt.setUTCDate(dt.getUTCDate() + 1); // 翌日の00:00
  dt.setUTCMilliseconds(-1); // 1ms前 = 23:59:59.999
  return dt.toISOString();
};

// 課金 / プランの判定ヘルパー
const isCharge = (r: RawOrder): boolean => {
  const any = r as any;

  // type があれば優先
  if (any.type === "charge") return true;
  if (any.type === "plan") return false;

  const planId = Number(any.planId ?? 0);
  const planName = (any.planName || any.plan || "") as string;

  const isPointPurchase =
    planName === "ポイント購入" || planName === "ポイントチャージ";

  // 「ポイント購入」は課金扱い
  if (isPointPurchase) return true;

  // planId が正値 & プラン名があるものはプラン扱い
  if (planId > 0 && planName) return false;

  const point = Number(any.pointSpent ?? 0);
  const hasPoint = Number.isFinite(point) && point > 0;
  if (hasPoint) return false;

  // それ以外で金額だけ立っていれば課金扱い
  const amount = Number(any.priceJPY ?? any.amount ?? 0);
  return Number.isFinite(amount) && amount > 0;
};

const isPlan = (r: RawOrder): boolean => !isCharge(r);

// Raw -> ChargeRow
const mapCharge = (r: RawOrder, idx: number): ChargeRow => {
  const any = r as any;
  const idRaw = any.id ?? any.orderId ?? `charge-${idx + 1}`;
  const id = String(idRaw);

  const buyer = any.buyer || any.userName || "—";
  const amountJPY = Number(any.priceJPY ?? any.amount ?? 0) || 0;
  const chargedAt: string =
    any.purchasedAt || any.date || any.createdAt || new Date().toISOString();

  // ★ アフィ情報はネストも含めて拾う
  const affiliateId =
    any.affiliateId ??
    any.affiliate_id ??
    any.affiliateID ??
    any.affiliate?.id ??
    any.affiliate?.affiliateId ??
    null;

  const affiliateName =
    any.affiliateName ??
    any.affiliate_name ??
    any.affiliate?.name ??
    null;

  return {
    id,
    userId: any.userId,
    buyer,
    email: any.email,
    amountJPY,
    chargedAt,
    affiliateId,
    affiliateName,
  };
};


// Raw -> PlanRow（ptベース）
const mapPlan = (r: RawOrder, idx: number): PlanRow => {
  const any = r as any;
  const idRaw = any.id ?? any.orderId ?? `plan-${idx + 1}`;
  const id = String(idRaw);

  const planName = any.planName || any.plan || any.plan_title || "（不明）";
  const buyer = any.buyer || any.userName || "—";

  // サーバーはプランの pt を amount に入れて返してくる
  let pt = Number(any.pointSpent ?? 0);
  if (!Number.isFinite(pt) || pt <= 0) {
    pt = Number(any.amount ?? 0) || 0;
  }

  const purchasedAt: string =
    any.purchasedAt || any.date || any.createdAt || new Date().toISOString();

  // ★ アフィ情報はネストも含めて拾う
  const affiliateId =
    any.affiliateId ??
    any.affiliate_id ??
    any.affiliateID ??
    any.affiliate?.id ??
    any.affiliate?.affiliateId ??
    null;

  const affiliateName =
    any.affiliateName ??
    any.affiliate_name ??
    any.affiliate?.name ??
    null;

  return {
    id,
    userId: any.userId,
    planId: any.planId,
    planName,
    amountPt: pt,
    purchasedAt,
    affiliateId,
    affiliateName,
    buyer,
  };
};


// 汎用フィルタ（期間適用）
function filterByDate<T>(
  rows: T[],
  from: string,
  to: string,
  getDate: (row: T) => string
): T[] {
  if (!from && !to) return rows;
  const lo = from ? startOf(from) : undefined;
  const hi = to ? endOf(to) : undefined;
  return rows.filter((r) => {
    const t = getDate(r);
    if (!t) return false;
    if (lo && t < lo) return false;
    if (hi && t > hi) return false;
    return true;
  });
}

// アフィリエイター表示用ヘルパー
const formatAffiliate = (
  affiliateId?: string | number | null,
  affiliateName?: string | null,
  affiliateNameMap?: Record<string, string>
) => {
  if (affiliateId === null || affiliateId === undefined || affiliateId === "") {
    return "—";
  }

  const idStr = String(affiliateId);
  const idLabel = `ID: ${idStr}`;

  // ★ まず注文側の affiliateName を優先
  const nameFromRow = affiliateName ? String(affiliateName).trim() : "";

  // ★ 次にマスタ辞書（/api/admin/affiliates）で補完
  const nameFromMap = affiliateNameMap?.[idStr]
    ? String(affiliateNameMap[idStr]).trim()
    : "";

  const finalName = nameFromRow || nameFromMap;

  if (finalName) {
    return `${finalName}（${idLabel}）`;
  }

  return `未登録（${idLabel}）`;
};


/* =========================
 * 画面本体
 * ========================= */
export default function Sales() {

  const navigate = useNavigate();

  // 生データ
  const [rawRows, setRawRows] = useState<RawOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
    // ★ affiliateId -> accountName の辞書（フロント補完用）
  const [affiliateNameMap, setAffiliateNameMap] = useState<Record<string, string>>({});


  const [tabIndex, setTabIndex] = useState(0);

  // 期間フィルタ（yyyy-mm-dd）
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  // ===== 注文データ取得 =====
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const res = await fetch("/api/admin/orders/history", {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

        const json = await res.json();
        const arr: RawOrder[] = Array.isArray(json?.items)
          ? json.items
          : Array.isArray(json)
          ? json
          : [];

        if (!aborted) setRawRows(arr);
      } catch (e: any) {
        if (!aborted) setErr(e?.message || "Failed to fetch");
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  // ===== アフィマスタ取得（ID -> 名前）=====
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/affiliates?status=all&query=", {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;

        const json = await res.json();
        const list: any[] = Array.isArray(json) ? json : Array.isArray(json?.items) ? json.items : [];

        const map: Record<string, string> = {};
        list.forEach((a: any) => {
          const id = String(a.affiliateId ?? a.code ?? "").trim();
          if (!id) return;
          const name = String(a.accountName ?? a.name ?? "").trim();
          if (name) map[id] = name;
        });

        if (!aborted) setAffiliateNameMap(map);
      } catch {
        // noop（マスタ取れなくてもID表示にフォールバック）
      }
    })();

    return () => {
      aborted = true;
    };
  }, []);


  // ===== 課金 / プランに分解 =====
  const chargeRows: ChargeRow[] = useMemo(
    () => rawRows.filter(isCharge).map(mapCharge),
    [rawRows]
  );

  const planRows: PlanRow[] = useMemo(
    () => rawRows.filter(isPlan).map(mapPlan),
    [rawRows]
  );

  /* =========================
   * 期間フィルタ適用後
   * ========================= */
  const filteredChargeRows = useMemo(
    () => filterByDate(chargeRows, from, to, (r) => r.chargedAt),
    [chargeRows, from, to]
  );

  const filteredPlanRows = useMemo(
    () => filterByDate(planRows, from, to, (r) => r.purchasedAt),
    [planRows, from, to]
  );

  /* =========================
   * 日別・月別 集計（課金 / プラン）
   * ========================= */
  const dailyData = useMemo(() => {
    const map = new Map<
      string,
      { date: string; chargeAmount: number; planAmount: number }
    >();

    for (const r of filteredChargeRows) {
      const date = fmtYmd(r.chargedAt);
      if (!date) continue;
      if (!map.has(date)) map.set(date, { date, chargeAmount: 0, planAmount: 0 });
      const rec = map.get(date)!;
      rec.chargeAmount += r.amountJPY || 0;
    }

    for (const r of filteredPlanRows) {
      const date = fmtYmd(r.purchasedAt);
      if (!date) continue;
      if (!map.has(date)) map.set(date, { date, chargeAmount: 0, planAmount: 0 });
      const rec = map.get(date)!;
      rec.planAmount += r.amountPt || 0;
    }

    return Array.from(map.values()).sort((a, b) =>
      a.date < b.date ? -1 : 1
    );
  }, [filteredChargeRows, filteredPlanRows]);

  const monthlyData = useMemo(() => {
    const map = new Map<
      string,
      { month: string; chargeAmount: number; planAmount: number }
    >();

    for (const r of filteredChargeRows) {
      const month = fmtYm(r.chargedAt);
      if (!month) continue;
      if (!map.has(month))
        map.set(month, { month, chargeAmount: 0, planAmount: 0 });
      const rec = map.get(month)!;
      rec.chargeAmount += r.amountJPY || 0;
    }

    for (const r of filteredPlanRows) {
      const month = fmtYm(r.purchasedAt);
      if (!month) continue;
      if (!map.has(month))
        map.set(month, { month, chargeAmount: 0, planAmount: 0 });
      const rec = map.get(month)!;
      rec.planAmount += r.amountPt || 0;
    }

    return Array.from(map.values()).sort((a, b) =>
      a.month < b.month ? -1 : 1
    );
  }, [filteredChargeRows, filteredPlanRows]);

  /* =========================
   * 目標金額（課金売上の目標）
   * ========================= */
  const DEFAULT_GOAL = 1_000_000;
  const [goal, setGoal] = useState<number>(() => {
    const saved = Number(localStorage.getItem("sales_goal_jpy") || "");
    return Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_GOAL;
  });
  const [goalInput, setGoalInput] = useState<string>(String(goal));
  const saveGoal = () => {
    const v = Number((goalInput || "").replace(/[, ]/g, ""));
    if (!Number.isFinite(v) || v <= 0) return;
    localStorage.setItem("sales_goal_jpy", String(v));
    setGoal(v);
  };

  /* =========================
   * サマリー（課金売上のみ）
   * ========================= */
  const summary = useMemo(() => {
    const actual = filteredChargeRows.reduce(
      (s, r) => s + (Number(r.amountJPY) || 0),
      0
    );
    const rate = goal > 0 ? actual / goal : 0;
    const remain = Math.max(goal - actual, 0);
    return { actual, rate, remain, count: filteredChargeRows.length };
  }, [filteredChargeRows, goal]);

  /* =========================
   * 月別グラフ（プランpt積み上げ + 課金線）
   * ========================= */
  const chartData = useMemo(() => {
    const byYmPlan = new Map<string, Map<string, number>>();
    const planTotal = new Map<string, number>();

    // プラン側：月×プランごとに pt 集計
    filteredPlanRows.forEach((r) => {
      const ym = fmtYm(r.purchasedAt);
      if (!ym) return;
      const p = r.planName || "（不明）";
      if (!byYmPlan.has(ym)) byYmPlan.set(ym, new Map());
      const m = byYmPlan.get(ym)!;
      const val = Number(r.amountPt) || 0;
      m.set(p, (m.get(p) || 0) + val);
      planTotal.set(p, (planTotal.get(p) || 0) + val);
    });

    // 課金側：月ごとの合計（円）
    const chargeByYm = new Map<string, number>();
    filteredChargeRows.forEach((r) => {
      const ym = fmtYm(r.chargedAt);
      if (!ym) return;
      chargeByYm.set(ym, (chargeByYm.get(ym) || 0) + (r.amountJPY || 0));
    });

    const monthSet = new Set<string>([
      ...byYmPlan.keys(),
      ...chargeByYm.keys(),
    ]);
    const months = Array.from(monthSet).sort();

    // 上位3プラン
    const top3 = [...planTotal.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name]) => name);

    const result: any[] = [];

    months.forEach((ym) => {
      const m = byYmPlan.get(ym);
      const rec: any = { month: ym, totalPt: 0, chargeAmount: 0 };
      let other = 0;

      if (m) {
        for (const [plan, v] of m) {
          if (top3.includes(plan)) rec[plan] = v;
          else other += v;
          rec.totalPt += v;
        }
      }

      if (other > 0) rec["その他"] = other;

      rec.chargeAmount = chargeByYm.get(ym) || 0;

      result.push(rec);
    });

    const barKeys = [...top3];
    if (result.some((r) => (r as any)["その他"] > 0)) barKeys.push("その他");

    return { data: result, barKeys };
  }, [filteredPlanRows, filteredChargeRows]);

  /* =========================
   * 課金履歴（タブ0表示用）
   * ========================= */
  const chargeHistoryRows = useMemo(
    () =>
      [...filteredChargeRows].sort(
        (a, b) => +new Date(b.chargedAt) - +new Date(a.chargedAt)
      ),
    [filteredChargeRows]
  );

  /* =========================
   * プラン別集計（タブ3）
   * ========================= */
  const planStats = useMemo(() => {
    const map = new Map<
      string,
      {
        count: number;
        totalPt: number;
        buyers: Set<string | number>;
        lastAt?: string;
      }
    >();

    for (const r of filteredPlanRows) {
      const name = r.planName || "（不明）";

      // 念のため「ポイント購入」は除外
      if (name === "ポイント購入" || name === "ポイントチャージ") continue;

      if (!map.has(name))
        map.set(name, {
          count: 0,
          totalPt: 0,
          buyers: new Set(),
          lastAt: undefined,
        });
      const b = map.get(name)!;
      b.count += 1;
      b.totalPt += Number(r.amountPt || 0);
      if (r.userId != null) b.buyers.add(r.userId);
      if (!b.lastAt || new Date(r.purchasedAt) > new Date(b.lastAt))
        b.lastAt = r.purchasedAt;
    }

    return [...map.entries()]
      .map(([plan, v]) => ({
        plan,
        count: v.count,
        totalPt: v.totalPt,
        uniqueBuyers: v.buyers.size,
        lastAt: v.lastAt
          ? v.lastAt.replace("T", " ").replace("Z", "")
          : "-",
      }))
      .sort((a, b) => b.totalPt - a.totalPt);
  }, [filteredPlanRows]);

  /* =========================
   * プラン購入者一覧（タブ4）
   * ========================= */
  const planBuyerRows = useMemo(
    () =>
      [...filteredPlanRows].sort(
        (a, b) => +new Date(b.purchasedAt) - +new Date(a.purchasedAt)
      ),
    [filteredPlanRows]
  );

  /* =========================
   * 期間プリセット
   * ========================= */
  const setPresetLast30 = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    const y = (d: Date) => d.getFullYear();
    const m = (d: Date) => String(d.getMonth() + 1).padStart(2, "0");
    const dd = (d: Date) => String(d.getDate()).padStart(2, "0");
    setFrom(`${y(start)}-${m(start)}-${dd(start)}`);
    setTo(`${y(end)}-${m(end)}-${dd(end)}`);
  };
  const setPresetThisMonth = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    setFrom(`${y}-${m}-01`);
    const last = new Date(y, now.getMonth() + 1, 0);
    setTo(`${y}-${m}-${String(last.getDate()).padStart(2, "0")}`);
  };
  const clearRange = () => {
    setFrom("");
    setTo("");
  };

  // 初回のみ：今月を期間プリセット
  useEffect(() => {
    if (!from && !to) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      setFrom(`${y}-${m}-01`);
      const last = new Date(y, now.getMonth() + 1, 0);
      setTo(`${y}-${m}-${String(last.getDate()).padStart(2, "0")}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
   * レンダリング
   * ========================= */
  return (
    <Box sx={{ p: 4 }}>
      {/* ===== サマリー ===== */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          📈 売上サマリー（課金ベース）
        </Typography>

        {/* 期間フィルタ */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ whiteSpace: "nowrap" }}>期間：</Typography>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <Typography>〜</Typography>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={setPresetLast30}>
              直近30日
            </Button>
            <Button size="small" variant="outlined" onClick={setPresetThisMonth}>
              今月
            </Button>
            <Button size="small" onClick={clearRange}>
              全期間
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} /> 読み込み中…
          </Box>
        ) : err ? (
          <Alert severity="error">読み込みに失敗しました：{err}</Alert>
        ) : (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 1 }}
            >
              <Typography>
                実績：
                <strong>
                  {summary.actual.toLocaleString()}
                  円
                </strong>
                　/　目標：
                <strong>{goal.toLocaleString()}円</strong>
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  type="text"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="目標金額を入力"
                  style={{
                    height: 32,
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                  }}
                  inputMode="numeric"
                />
                <Button size="small" variant="outlined" onClick={saveGoal}>
                  保存
                </Button>
              </Stack>
            </Stack>

            <Typography>
              達成率：
              <strong>{(summary.rate * 100).toFixed(2)}%</strong>　
              （課金件数：{summary.count.toLocaleString()}件）
            </Typography>
            <Typography>
              残り金額：
              <strong>{summary.remain.toLocaleString()}円</strong>
            </Typography>
          </>
        )}
      </Paper>

      {/* ===== 月別グラフ（プランpt積み上げ + 課金線） ===== */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📊 月別プラン消費（pt）＋ 課金売上（円）
        </Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(v: any, name: any) => {
                  const num = Number(v) || 0;
                  if (name === "課金売上（円）") {
                    return [num.toLocaleString() + "円", String(name)];
                  }
                  return [num.toLocaleString() + "pt", String(name)];
                }}
              />
              <Legend />
              {/* プラン側の積み上げバー（pt） */}
              {chartData.barKeys.map((k, i) => {
                const colorMap: Record<string, string> = {
                  ゴールドラッシュ: "#E6B422",
                  ブーストギア: "#0EA5E9",
                  スタータープラン: "#22C55E",
                  その他: "#FB923C",
                };
                const palette = [
                  "#E6B422",
                  "#0EA5E9",
                  "#22C55E",
                  "#FB923C",
                  "#F472B6",
                  "#A78BFA",
                ];
                const fill = colorMap[k] || palette[i % palette.length];
                return (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="plans"
                    fill={fill}
                    name={k}
                  />
                );
              })}
              {/* 課金売上線（円） */}
              <Line
                type="monotone"
                dataKey="chargeAmount"
                name="課金売上（円）"
                stroke="#64748B"
                strokeWidth={2}
                dot={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ===== 詳細タブ ===== */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          📂 売上詳細
        </Typography>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="課金履歴" />
          <Tab label="日別売上" />
          <Tab label="月間売上" />
          <Tab label="プラン購入数" />
          <Tab label="プラン購入者" />
        </Tabs>

        {/* タブ0：課金履歴 */}
        {tabIndex === 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>購入者</TableCell>
                  <TableCell align="right">課金額（円）</TableCell>
                  <TableCell>課金日</TableCell>
                  <TableCell>アフィリエイター</TableCell>
                  <TableCell align="center">購入履歴</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chargeHistoryRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "#888" }}>
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
                {chargeHistoryRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.buyer || "—"}</TableCell>
                    <TableCell align="right">
                      {r.amountJPY.toLocaleString()}円
                    </TableCell>
                    <TableCell>{fmtYmd(r.chargedAt)}</TableCell>
<TableCell>
  {formatAffiliate(r.affiliateId, r.affiliateName, affiliateNameMap)}
</TableCell>


                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!r.userId}
                        onClick={() =>
                          r.userId &&
                          navigate(`/admin/users?focusUser=${r.userId}`)
                        }
                      >
                        購入履歴
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* タブ1：日別売上（課金＆プラン） */}
        {tabIndex === 1 && (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(v: any, name: any) => {
                    const num = Number(v) || 0;
                    if (name === "課金売上（円）") {
                      return [num.toLocaleString() + "円", String(name)];
                    }
                    return [num.toLocaleString() + "pt", String(name)];
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="chargeAmount"
                  name="課金売上（円）"
                  stroke="#0EA5E9"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="planAmount"
                  name="プラン消費（pt）"
                  stroke="#E6B422"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* タブ2：月間売上（課金＆プラン） */}
        {tabIndex === 2 && (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip
                  formatter={(v: any, name: any) => {
                    const num = Number(v) || 0;
                    if (name === "課金売上（円）") {
                      return [num.toLocaleString() + "円", String(name)];
                    }
                    return [num.toLocaleString() + "pt", String(name)];
                  }}
                />
                <Legend />
                <Bar
                  dataKey="planAmount"
                  name="プラン消費（pt）"
                  fill="#E6B422"
                />
                <Bar
                  dataKey="chargeAmount"
                  name="課金売上（円）"
                  fill="#0EA5E9"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* タブ3：プラン購入数 */}
        {tabIndex === 3 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>プラン</TableCell>
                  <TableCell align="right">購入件数</TableCell>
                  <TableCell align="right">消費合計（pt）</TableCell>
                  <TableCell align="right">ユニーク購入者数</TableCell>
                  <TableCell>最終購入日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "#888" }}>
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
                {planStats.map((r) => (
                  <TableRow key={r.plan}>
                    <TableCell>{r.plan}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                    <TableCell align="right">
                      {r.totalPt.toLocaleString()}pt
                    </TableCell>
                    <TableCell align="right">{r.uniqueBuyers}</TableCell>
                    <TableCell>{r.lastAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* タブ4：プラン購入者 */}
        {tabIndex === 4 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>購入者</TableCell>
                  <TableCell>プラン</TableCell>
                  <TableCell align="right">消費pt</TableCell>
                  <TableCell>購入日</TableCell>
                  <TableCell>アフィリエイター</TableCell>
                  <TableCell align="center">購入履歴</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planBuyerRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "#888" }}>
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
                {planBuyerRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.buyer || "—"}</TableCell>
                    <TableCell>{r.planName}</TableCell>
                    <TableCell align="right">
                      {r.amountPt.toLocaleString()}pt
                    </TableCell>
                    <TableCell>{fmtYmd(r.purchasedAt)}</TableCell>
<TableCell>
  {formatAffiliate(r.affiliateId, r.affiliateName, affiliateNameMap)}
</TableCell>


                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!r.userId}
                        onClick={() =>
                          r.userId &&
                          navigate(`/admin/users?focusUser=${r.userId}`)
                        }
                      >
                        購入履歴
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}7
      </Paper>
    </Box>
  );
}